/**
 * Typed wrappers for the note version-history tools (note-history epic Track 2, 2026-06-22).
 *
 * Thin calls through `callTool` (same envelope-unwrap + camelCase mapping as the rest of the
 * tool surface). Backs `<NoteHistoryPanel>`. Response keys arrive camelCased via the shared
 * client mapper; request bodies use the snake-case field names the backend request models take.
 */

import { callTool, type ToolResult } from './client';
import type { NoteId } from '@/types';

/** One revision in the history list — provenance + a short preview, no full body. */
export interface NoteRevisionItem {
  revisionId: string;
  noteId: string;
  /** Who made the edit that superseded this state: rest | mcp | router | system. */
  actor: string;
  /** The tool that made the change: update_note | replace_in_note | restore_note_revision | … */
  changeKind: string;
  capturedAt: string;
  noteUpdatedAt: string | null;
  charCount: number;
  preview: string;
  contentHash: string;
}

export interface NoteRevisionList {
  noteId: string;
  total: number;
  items: NoteRevisionItem[];
}

/** One revision's full snapshot — for preview / diff / restore. */
export interface NoteRevisionDetail {
  revisionId: string;
  noteId: string;
  title: string;
  body: string;
  actor: string;
  changeKind: string;
  capturedAt: string;
  noteUpdatedAt: string | null;
  contentHash: string;
}

export interface RevisionSaveResult {
  noteId: string;
  updatedAt: string;
  extractionStatus: string;
}

export const listNoteRevisions = (
  noteId: NoteId,
  limit = 50,
  offset = 0,
): Promise<ToolResult<NoteRevisionList>> =>
  callTool('list_note_revisions', { id: noteId, limit, offset });

export const getNoteRevision = (
  noteId: NoteId,
  revisionId: string,
): Promise<ToolResult<NoteRevisionDetail>> =>
  callTool('get_note_revision', { id: noteId, revision_id: revisionId });

export const restoreNoteRevision = (
  noteId: NoteId,
  revisionId: string,
): Promise<ToolResult<RevisionSaveResult>> =>
  callTool('restore_note_revision', { id: noteId, revision_id: revisionId });
